var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// db/files.ts
import { A } from "@adornis/base/env-info";
import { Int } from "@adornis/baseql/baseqlTypes";
import { BaseQLClient } from "@adornis/baseql/client/baseqlClient";
import { BaseQLWebsocketTransport } from "@adornis/baseql/client/transports/websocketTransport";
import { Arg, Entity, Field, Mutation, Query } from "@adornis/baseql/decorators";
import { AdornisEntity } from "@adornis/baseql/entities/adornisEntity";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity";
import { getCollection } from "@adornis/baseql/server/collections";
import { selectionSet } from "@adornis/baseql/utils/queryGeneration";
import { nonOptional } from "@adornis/validation/functions/nonOptional";
import { validate } from "@adornis/validation/decorators";
import { exec } from "child_process";
import { createReadStream, lstat, unlink, writeFile } from "fs";
import { join } from "path";
import { createHash } from "crypto";
import "sharp";
var AdornisFileMeta = class extends AdornisEntity {
};
AdornisFileMeta._class = "FileMeta";
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], AdornisFileMeta.prototype, "fileName", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], AdornisFileMeta.prototype, "name", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => Int)
], AdornisFileMeta.prototype, "size", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], AdornisFileMeta.prototype, "mimetype", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], AdornisFileMeta.prototype, "md5", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], AdornisFileMeta.prototype, "filePath", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], AdornisFileMeta.prototype, "extension", 2);
AdornisFileMeta = __decorateClass([
  Entity()
], AdornisFileMeta);
var AdornisFile = class extends MongoEntity {
  constructor(doc) {
    super(doc);
  }
  static get allFields() {
    return selectionSet(() => this, 2);
  }
  getFullPath(version = "original") {
    if (version === "original") {
      if (!this.meta)
        return "";
      if (A.isServer)
        return join(this.meta.filePath, this._id + "." + this.meta.extension);
      return this.constructor.join(this.meta.filePath, this._id + "." + this.meta.extension);
    } else {
      const fileVersion = this.versions.find((v) => v.name === version);
      if (!fileVersion)
        return "";
      if (A.isServer)
        return join(fileVersion.filePath, this._id + "_" + version + "." + fileVersion.extension);
      return this.constructor.join(
        fileVersion.filePath,
        this._id + "_" + version + "." + fileVersion.extension
      );
    }
  }
  getMimeType(version = "original") {
    if (version === "original")
      return this.meta?.mimetype;
    return this.versions.find((v) => v.name === version)?.mimetype;
  }
  getDownloadLink(version = "original", size, options) {
    return this.constructor.getDownloadLink(this._id, version, size, options);
  }
  getServeLink(version = "original", size, options) {
    return this.constructor.getServeLink(this._id, version, size, options);
  }
  static removeFile(id, version) {
    return async (gqlFields) => {
      const collection = await getCollection(this._collectionName);
      let file = await collection.findOne({ _id: id });
      if (!file)
        throw new Error(`No AdornisFile with id ${id} found!`);
      file = new (this.metaTreeNode.type())(file);
      console.log("removing: ", id, version, file.versions);
      if (version)
        await file.removeVersion(version);
      else
        await Promise.all(file.versions.map((version2) => file.removeVersion(version2.name)));
      console.log("removing db entry?: ", file.versions.length, file.versions);
      if (!version) {
        await file.removeFilePath(file.getFullPath());
        await MongoEntity.removeEntity(file._class, file._id)("");
      }
      return true;
    };
  }
  removeVersion(version) {
    if (A.isClient)
      throw new Error("AdornisFile.removeVersion can only be called on the server!");
    const versionIndex = this.versions.findIndex((v) => v.name === version);
    if (versionIndex === -1)
      return false;
    console.log("removing version: ", version, this.getFullPath(version));
    return new Promise(async (resolve, reject) => {
      if (!this.getFullPath(version)) {
        console.warn("Couldn't getFullPath ", version);
        this.versions.splice(versionIndex, 1);
        resolve(true);
      } else {
        this.removeFilePath(this.getFullPath(version));
        this.versions.splice(versionIndex);
        resolve(true);
      }
    });
  }
  removeFilePath(path) {
    if (A.isClient)
      throw new Error("AdornisFile.removeFilePath can only be called on the server!");
    return new Promise(
      (resolve, reject) => lstat(path, async (err) => {
        if (!err)
          unlink(path, async (err2) => {
            if (err2) {
              console.warn("Couldn't unlink ", path, err2);
              reject(err2);
            } else {
              resolve(true);
            }
          });
        else {
          console.warn("Couldn't lstat ", path, err);
          resolve(true);
        }
      })
    );
  }
  async upload(file, { acceptFileTypes, maxImageSize, additionalInfo = {} } = {}) {
    if (A.isClient) {
      await this.save();
      const formData = new FormData();
      if (acceptFileTypes && !acceptFileTypes.split(",").includes("." + file.name.split(".").slice(-1)[0]))
        throw new Error("selected file has no valid accepted file type");
      if (maxImageSize) {
        const reduce = (await import("image-blob-reduce")).default();
        await reduce.to_blob(file, { max: maxImageSize }).then((blob) => {
          const fileFromBlob = new File([blob], file.name, {
            lastModified: file.lastModified,
            type: file.type
          });
          formData.append("_file", fileFromBlob.size < file.size ? fileFromBlob : file);
        });
      } else
        formData.append("_file", file);
      formData.append("_file_id", this._id);
      additionalInfo.sessionID = BaseQLClient.sessionID;
      additionalInfo.name = file.name;
      formData.append("_file_additional_info", JSON.stringify(additionalInfo));
      return await this.constructor.uploadFormData(formData);
    } else
      throw new Error("FileInstance.upload can only be called on client");
  }
  async addVersion(versionName, file, { acceptFileTypes, maxImageSize, additionalInfo = {} } = {}) {
    if (A.isClient) {
      await this.save();
      const formData = new FormData();
      if (acceptFileTypes && !acceptFileTypes.split(",").includes("." + file.name.split(".").slice(-1)[0]))
        throw new Error("selected file has no valid accepted file type");
      if (maxImageSize) {
        const reduce = (await import("image-blob-reduce")).default();
        await reduce.to_blob(file, { max: maxImageSize }).then((blob) => {
          const fileFromBlob = new File([blob], file.name, {
            lastModified: file.lastModified,
            type: file.type
          });
          formData.append("_file", fileFromBlob.size < file.size ? fileFromBlob : file);
        });
      } else
        formData.append("_file", file);
      formData.append("_file_id", this._id);
      formData.append("_file_version", versionName);
      additionalInfo.sessionID = BaseQLClient.sessionID;
      formData.append("_file_additional_info", JSON.stringify(additionalInfo));
      return this.constructor.uploadFormData(formData);
    } else
      throw new Error("FileInstance.addVersion can only be called on client");
  }
  async addPath(filePath, version) {
    const stat = await this.constructor.getFileStats(filePath);
    if (stat) {
      const md5 = await new Promise((resolve) => {
        const hashAlgo = createHash("md5");
        const readStream = createReadStream(filePath);
        readStream.on("data", (data) => hashAlgo.update(data));
        readStream.on("end", () => {
          resolve(hashAlgo.digest("hex"));
        });
      });
      const extension = filePath.split("/").slice(-1)[0].split(".")[1];
      const basePath = filePath.split("/").slice(0, -1).join("/");
      const meta = new AdornisFileMeta({
        fileName: filePath.split("/").slice(-1)[0].split(".")[0],
        name: version ? version : filePath.split("/").slice(-1)[0].split(".")[0],
        size: stat.size,
        mimetype: await new Promise(
          (resolve) => exec(`file --mime-type -b ${filePath}`, (_, stdout) => resolve(stdout.trim()))
        ),
        md5,
        filePath: filePath.split("/").slice(0, -1).join("/") + "/",
        extension: filePath.split("/").slice(-1)[0].split(".")[1]
      });
      if (!version)
        this.meta = meta;
      else {
        const existingVersionIndex = this.versions.findIndex((ver) => ver.name === version);
        if (existingVersionIndex !== -1)
          this.versions[existingVersionIndex] = meta;
        else
          this.versions.push(meta);
      }
      await new Promise(
        (resolve) => exec(`mv ${filePath} ${join(basePath, "/" + this._id + "_" + version + "." + extension)}`, () => resolve())
      );
      await this.save();
    } else
      throw new Error("File you wanted to add does not exists");
  }
  static async writeBufferToDiskAndGetMeta({
    buffer,
    name,
    extension,
    version,
    basePath = process.env.DATADIR || "/tmp/",
    id = A.getGloballyUniqueID()
  }) {
    const path = join(basePath, `${id}.${extension}`);
    const savedFile = await new Promise(
      (resolve) => writeFile(path, buffer, async (err) => {
        if (err) {
          console.error("Error creating file entry from buffer", err);
          return resolve(false);
        }
        console.log("written file to disk:", path);
        resolve(true);
      })
    );
    if (!savedFile)
      return {};
    const mimetype = await new Promise(
      (resolve) => exec(`file --mime-type -b ${path}`, (_, stdout) => resolve(stdout.trim()))
    );
    const hashAlgo = createHash("md5");
    hashAlgo.update(buffer);
    const md5 = hashAlgo.digest("hex");
    const fileStat = await this.getFileStats(path);
    if (!fileStat)
      throw new Error("Something went wrong creating the file");
    const meta = new AdornisFileMeta({
      fileName: name,
      name: version || name,
      size: fileStat.size,
      mimetype,
      md5,
      filePath: basePath,
      extension
    });
    return { meta, id };
  }
  static async createFromBuffer(buffer, name, extension, version, basePath = process.env.DATADIR || "/tmp/") {
    const { meta, id } = await this.writeBufferToDiskAndGetMeta({ buffer, name, extension, version, basePath });
    if (!meta)
      throw new Error("Error while writing buffer to disk");
    const instance = new this({ _id: id, meta });
    await instance.create();
    return instance;
  }
  async updateFromBuffer(buffer, name, extension, version, basePath = process.env.DATADIR || "/tmp/") {
    const { meta } = await this.constructor.writeBufferToDiskAndGetMeta({
      buffer,
      name,
      extension,
      version,
      basePath,
      id: this._id
    });
    if (meta) {
      if (!version)
        this.meta = meta;
      else {
        const existingVersionIndex = this.versions.findIndex((ver) => ver.name === version);
        if (existingVersionIndex !== -1)
          this.versions[existingVersionIndex] = meta;
        else
          this.versions.push(meta);
      }
      await this.save();
      return this;
    }
  }
  static getServeLink(fileID, version = "original", size, options) {
    if (!fileID)
      return "";
    return this.absoluteUrl(
      `${this.join(...[this._serveEndpoint, version, fileID, ...size ? [size] : []])}${options ? "?" + new URLSearchParams(Object.entries(options)).toString() : ""}`
    );
  }
  static getDownloadLink(fileID, version = "original", size, options) {
    if (!fileID)
      return "";
    return this.absoluteUrl(
      `${this.join(...[this._downloadEndpoint, version, fileID, ...size ? [size] : []])}${options ? "?" + new URLSearchParams(Object.entries(options)).toString() : ""}`
    );
  }
  static join(...pathParts) {
    return "/" + pathParts.map((pathPart) => pathPart.replace(/^\/|\/$/g, "")).join("/");
  }
  static getFileByPath(fullPath) {
    return (gqlFields) => {
      const fullName = fullPath.split("/").slice(-1)[0];
      const id = fullName.split(".")[0];
      return this.getByID(id)(this.allFields);
    };
  }
  static async upload(file, { acceptFileTypes, maxImageSize, additionalInfo = {} } = {}) {
    if (A.isClient) {
      const formData = new FormData();
      if (acceptFileTypes && !acceptFileTypes.split(",").includes("." + file.name.split(".").slice(-1)[0].toLowerCase()))
        throw new Error(
          "selected file has no valid file type, was: " + file.name.split(".").slice(-1)[0].toLowerCase()
        );
      if (maxImageSize) {
        const reduce = (await import("image-blob-reduce")).default();
        await reduce.toBlob(file, { max: maxImageSize }).then((blob) => {
          const fileFromBlob = new File([blob], file.name, {
            lastModified: file.lastModified,
            type: file.type
          });
          formData.append("_file", fileFromBlob.size < file.size ? fileFromBlob : file);
        }).catch(console.warn);
      } else
        formData.append("_file", file);
      additionalInfo.sessionID = BaseQLClient.sessionID;
      additionalInfo.name = file.name;
      console.log("upload file: ", additionalInfo);
      formData.append("_file_additional_info", JSON.stringify(additionalInfo));
      return this.uploadFormData(formData);
    } else
      throw new Error("File.upload can only be called on client");
  }
  static async getFileStats(path) {
    if (A.isServer)
      return new Promise(
        (resolve) => lstat(path, (err, stats) => {
          if (err)
            resolve(void 0);
          resolve(stats);
        })
      );
    else
      throw new Error("Cannot use checkFileExists on client");
  }
  static uploadFormData(formData) {
    return new Promise(async (resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", this.absoluteUrl(this._uploadEndpoint), true);
      xhr.onloadend = (e) => {
        const res = xhr.responseText;
        if (res.includes("<html>"))
          return reject(new Error(`Expected upload response but got html: ${res}`));
        try {
          const parsedRes = JSON.parse(res);
          if (parsedRes.status === "error")
            return reject(new Error(`${parsedRes.detail} ${parsedRes.message} ${parsedRes.stack}`));
          if (parsedRes.status === "success")
            resolve(parsedRes.id);
        } catch (error) {
          reject(new Error(`An error occurred while parsing the upload response: ${res}`));
        }
      };
      xhr.onerror = (e) => reject(e);
      xhr.send(formData);
    });
  }
  static absoluteUrl(path = "/") {
    const transportRootUrl = BaseQLWebsocketTransport?.instance?.client.url.match(/http[s]*:\/\/[^\/]+/)[0] ?? A.absoluteUrl();
    const endpoint = `${transportRootUrl.slice(-1) === "/" ? transportRootUrl.slice(0, -1) : transportRootUrl}${path.startsWith("/") ? path : "/" + path}`;
    return endpoint;
  }
};
AdornisFile._collectionName = "files";
AdornisFile._class = "File";
AdornisFile._uploadEndpoint = "/api/_upload";
AdornisFile._serveEndpoint = "/api/_file_serve/";
AdornisFile._downloadEndpoint = "/api/_file_download/";
__decorateClass([
  Field((type) => AdornisFileMeta, {})
], AdornisFile.prototype, "meta", 2);
__decorateClass([
  Field((type) => [AdornisFileMeta], { default: (v) => v ?? [] })
], AdornisFile.prototype, "versions", 2);
__decorateClass([
  Field((type) => Date, { default: (v) => v ?? new Date() })
], AdornisFile.prototype, "createdAt", 2);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("id", (type) => String)),
  __decorateParam(1, Arg("version", (type) => String))
], AdornisFile, "removeFile", 1);
__decorateClass([
  Query((type) => AdornisFile),
  __decorateParam(0, Arg("fullPath", (type) => String))
], AdornisFile, "getFileByPath", 1);
AdornisFile = __decorateClass([
  Entity()
], AdornisFile);
var dataURLtoFile = (dataurl, filename) => {
  var arr = dataurl.split(","), mime = arr[0].match(/:(.*?);/)?.[1], bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};
if (A.isClient && A.isDevelopment && !A.isServiceWorker)
  window.AdornisFile = AdornisFile;
export {
  AdornisFile,
  AdornisFileMeta,
  dataURLtoFile
};
